import type { ReactNode } from 'react';
import { type Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  Page,
  Progress,
  RoutedTabs,
  WarningPanel,
} from '@backstage/core-components';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { Box, makeStyles } from '@material-ui/core';
import { CatalogDefectsBanner } from './CatalogDefectsBanner';
import { ComponentHeader } from './ComponentHeader';
import {
  ComponentPageProvider,
  useComponentPage,
  type ComponentPageData,
} from './ComponentPageContext';

/** One tab of a component's page. */
export type ComponentTab = {
  /** Today's addresses: `/`, `/ci-cd`, `/pull-requests`, `/api`, … */
  path: string;
  title: string;
  /** The tab is shown only when this says so — by data, not by type. */
  if?: (data: ComponentPageData) => boolean;
  /** A count beside the title — "100+" for a floor; undefined shows none. */
  count?: (data: ComponentPageData) => number | string | undefined;
  content: ReactNode;
};

const useStyles = makeStyles(theme => ({
  page: {
    /*
      Backstage's tab strip is made for its stock header, a band across the
      page's full width: it fills its row in the paper colour and indents only
      its left side. This page's header sits inside the content's margins, and
      everything on it is a card on the page's own ground, so the strip becomes
      one too: inside the margins, framed as the Overview's columns are, and
      as wide as they are together (DX-520).

      Found by position, as the element right before the tab's content, the
      page's one <article>: its own class carries a counter under nested
      themes. The margins are the content's: 16px on a phone, 24px above.
    */
    '& > :has(+ article)': {
      paddingLeft: 0,
      margin: theme.spacing(0, 2),
      border: '1px solid var(--ui-border)',
      borderRadius: 8,
      background: 'var(--ui-bg-elevated)',
      // The selected tab's underline and a hovered tab's fill follow the
      // card's rounded corners rather than poking out of them. `clip`, not
      // `hidden`: hidden makes the strip a scroll container, which the page's
      // grid may shrink to nothing once the tab below is taller than the
      // window — on a desktop the strip collapsed to its border.
      overflow: 'clip',
      [theme.breakpoints.up('sm')]: { margin: theme.spacing(0, 3) },
    },
  },
  count: {
    marginLeft: theme.spacing(0.25),
    padding: '0 6px',
    borderRadius: 10,
    fontSize: '0.72rem',
    lineHeight: '18px',
    background: 'var(--ui-bg-muted)',
    color: 'var(--ui-text-muted)',
  },
}));

const TabLabel = ({
  title,
  count,
}: {
  title: string;
  count?: number | string;
}) => {
  const classes = useStyles();
  return (
    <span>
      {title}
      {/* The space keeps the count its own word to a screen reader. */}
      {count !== undefined && (
        <>
          {' '}
          <span className={classes.count}>{count}</span>
        </>
      )}
    </span>
  );
};

const Tabs = ({ tabs }: { tabs: ComponentTab[] }) => {
  const data = useComponentPage();
  const routes = tabs
    .filter(tab => !tab.if || tab.if(data))
    .map(tab => {
      const count = tab.count?.(data);
      return {
        path: tab.path,
        title: tab.title,
        // The label carries the count; `title` stays plain for the page title.
        tabProps: { label: <TabLabel title={tab.title} count={count} /> },
        children: (
          <>
            <CatalogDefectsBanner />
            {tab.content}
          </>
        ),
      };
    });
  return <RoutedTabs routes={routes} />;
};

const Layout = ({ entity, tabs }: { entity: Entity; tabs: ComponentTab[] }) => (
  <ComponentPageProvider entity={entity}>
    <ComponentHeader />
    <Tabs tabs={tabs} />
  </ComponentPageProvider>
);

/**
 * A component's page — service, library or website alike (specs/component-page.md).
 *
 * Built from the same parts as the catalog's own entity layout — the page, the
 * routed tabs, the inspect and unregister dialogs — with the portal's own header
 * in place of the stock one, and the catalog-defects banner above every tab.
 * API, System, Group, User and Domain pages keep the stock layout.
 */
export const ComponentLayout = ({ tabs }: { tabs: ComponentTab[] }) => {
  const classes = useStyles();
  const { entity, loading, error } = useAsyncEntity();
  return (
    <Page
      themeId={(entity?.spec?.type as string | undefined) ?? 'home'}
      className={classes.page}
    >
      {loading && <Progress />}
      {error && (
        <Box p={3}>
          <WarningPanel title="Could not load this component">
            {error.message}
          </WarningPanel>
        </Box>
      )}
      {/* Keyed by the entity, so following a link to another component
          starts its page afresh instead of showing the last one's answers
          until its own arrive. */}
      {entity && (
        <Layout key={stringifyEntityRef(entity)} entity={entity} tabs={tabs} />
      )}
    </Page>
  );
};
